import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Renders the dry-run report returned by the Edge Function.
 * Light theme, minimalist. Each list (managers/creators/departures) is collapsible.
 */
const Stat = ({ label, value, accent = false }) => (
  <div className="flex flex-col gap-1 p-4 rounded-xl bg-muted/40 border border-border">
    <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`text-2xl font-heading font-bold tabular-nums ${accent ? 'text-primary' : 'text-foreground'}`}>
      {value ?? '—'}
    </div>
  </div>
);

const Section = ({ title, count, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between text-start"
      >
        <div className="font-medium text-foreground">{title}</div>
        <div className="flex items-center gap-3">
          <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{count ?? 0}</span>
          <span className="text-muted-foreground text-sm">{open ? '▾' : '▸'}</span>
        </div>
      </button>
      {open && (
        <div className="border-t border-border max-h-72 overflow-auto">
          {children}
        </div>
      )}
    </div>
  );
};

const DryRunReport = ({ report, onCancel, onApply, applying = false }) => {
  const { t } = useTranslation();
  if (!report) return null;

  return (
    <div className="space-y-5">
      {/* Header strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          label={t('tcn.dryRun.language', 'Language')}
          value={(report.language_detected ?? '').toUpperCase()}
          accent
        />
        <Stat
          label={t('tcn.dryRun.mode', 'Mode')}
          value={report.mode}
        />
        <Stat
          label={t('tcn.dryRun.period', 'Period')}
          value={report.period_start && report.period_end
            ? <span dir="ltr">{`${report.period_start} → ${report.period_end}`}</span>
            : '—'}
        />
        <Stat
          label={t('tcn.dryRun.rows', 'Rows total')}
          value={report.rows_total}
          accent
        />
      </div>

      {/* Counters row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={t('tcn.dryRun.creatorsCreate', 'Creators to create')}     value={report.creators_to_create?.length ?? 0} />
        <Stat label={t('tcn.dryRun.creatorsUpdate', 'Creators to update')}     value={report.creators_to_update ?? 0} />
        <Stat label={t('tcn.dryRun.managersCreate', 'Managers to create')}     value={report.managers_to_create?.length ?? 0} />
        <Stat label={t('tcn.dryRun.managersUpdate', 'Managers to update')}     value={report.managers_to_update ?? 0} />
      </div>

      {/* Warnings */}
      {(report.warnings?.length ?? 0) > 0 && (
        <div className="rounded-xl border border-warning/30 bg-warning/5 p-4">
          <div className="text-sm font-medium text-warning mb-1">
            {t('tcn.dryRun.warnings', 'Warnings')}
          </div>
          <ul className="text-xs text-foreground/80 list-disc list-inside space-y-0.5">
            {report.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Errors */}
      {(report.errors?.length ?? 0) > 0 && (
        <div className="rounded-xl border border-error/30 bg-error/5 p-4">
          <div className="text-sm font-medium text-error mb-1">
            {t('tcn.dryRun.errors', 'Errors')}
          </div>
          <ul className="text-xs text-foreground/80 list-disc list-inside space-y-0.5">
            {report.errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* Sections */}
      <Section
        title={t('tcn.dryRun.managersToCreate', 'New managers (will receive magic-link invites)')}
        count={report.managers_to_create?.length ?? 0}
      >
        <div className="divide-y divide-border">
          {(report.managers_to_create ?? []).map((m, i) => (
            <div key={i} className="px-4 py-2 text-sm flex justify-between items-center">
              <div className="font-mono">{m.email}</div>
              <div className="text-muted-foreground text-xs">{m.display_name ?? '—'}</div>
            </div>
          ))}
          {(report.managers_to_create?.length ?? 0) === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground">{t('tcn.dryRun.empty', 'None')}</div>
          )}
        </div>
      </Section>

      <Section
        title={t('tcn.dryRun.creatorsToCreate', 'New creators')}
        count={report.creators_to_create?.length ?? 0}
      >
        <div className="divide-y divide-border">
          {(report.creators_to_create ?? []).slice(0, 200).map((c, i) => (
            <div key={i} className="px-4 py-2 text-sm flex justify-between items-center">
              <div>
                <div className="font-medium">{c.username}</div>
                <div className="font-mono text-xs text-muted-foreground">{c.tiktok_id}</div>
              </div>
              <div className="text-xs text-muted-foreground">{c.manager_email ?? '—'}</div>
            </div>
          ))}
          {(report.creators_to_create?.length ?? 0) === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground">{t('tcn.dryRun.empty', 'None')}</div>
          )}
          {(report.creators_to_create?.length ?? 0) > 200 && (
            <div className="px-4 py-2 text-xs text-muted-foreground italic">
              {t('tcn.dryRun.truncated', 'Showing first 200…')}
            </div>
          )}
        </div>
      </Section>

      <Section
        title={t('tcn.dryRun.signalA', 'QUIT detected — Signal A (will be marked departed)')}
        count={report.creators_to_mark_departed_signal_a?.length ?? 0}
      >
        <div className="divide-y divide-border">
          {(report.creators_to_mark_departed_signal_a ?? []).map((c, i) => (
            <div key={i} className="px-4 py-2 text-sm flex justify-between items-center">
              <div className="font-medium">{c.username}</div>
              <div className="font-mono text-xs text-muted-foreground">{c.tiktok_id}</div>
            </div>
          ))}
          {(report.creators_to_mark_departed_signal_a?.length ?? 0) === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground">{t('tcn.dryRun.empty', 'None')}</div>
          )}
        </div>
      </Section>

      <Section
        title={t('tcn.dryRun.signalB', 'Absent from file — Signal B (will be marked departed)')}
        count={report.creators_to_mark_departed_signal_b?.length ?? 0}
      >
        <div className="divide-y divide-border">
          {(report.creators_to_mark_departed_signal_b ?? []).map((c, i) => (
            <div key={i} className="px-4 py-2 text-sm flex justify-between items-center">
              <div className="font-medium">{c.username ?? '—'}</div>
              <div className="font-mono text-xs text-muted-foreground">{c.tiktok_id}</div>
            </div>
          ))}
          {(report.creators_to_mark_departed_signal_b?.length ?? 0) === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground">{t('tcn.dryRun.empty', 'None')}</div>
          )}
        </div>
      </Section>

      <Section
        title={t('tcn.dryRun.unrecognized', 'Unrecognized headers')}
        count={report.unrecognized_headers?.length ?? 0}
      >
        <div className="divide-y divide-border">
          {(report.unrecognized_headers ?? []).map((h, i) => (
            <div key={i} className="px-4 py-2 text-sm font-mono">
              col {h.col} — {h.raw}
            </div>
          ))}
          {(report.unrecognized_headers?.length ?? 0) === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground">{t('tcn.dryRun.empty', 'None')}</div>
          )}
        </div>
      </Section>

      {/* File hash + actions */}
      <div className="rounded-xl border border-border bg-card p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          <div>{t('tcn.dryRun.file', 'File')}: <span className="font-mono">{report.file_name}</span></div>
          <div className="font-mono mt-0.5 break-all">SHA-256: {report.file_hash_sha256}</div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={applying}
            className="px-4 py-2 rounded-lg border border-border bg-card hover:bg-muted text-foreground text-sm font-medium disabled:opacity-50"
          >
            {t('tcn.dryRun.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={applying || (report.errors?.length ?? 0) > 0}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold disabled:opacity-50"
          >
            {applying
              ? t('tcn.dryRun.applying', 'Applying…')
              : t('tcn.dryRun.apply', 'Send invites & Apply')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DryRunReport;
